// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { Database } from 'lucide-react'
import { usePmsName } from '../../auth/AuthContext'

/**
 * Compact "Synced X minutes ago" chip. Tone goes yellow if older than the
 * `warnAfterMin` threshold (default 30) and red after `staleAfterMin`
 * (default 240). Use on Rx detail headers and queue rows so users can tell
 * how recently this row was synced from the tenant's dispensing system (ADR-014).
 * D-PMS-1: vendor name comes from the tenant, not a hardcoded PioneerRx.
 */
export default function FreshnessChip({
  syncedAt,
  warnAfterMin = 30,
  staleAfterMin = 240,
}: {
  syncedAt: string | null | undefined
  warnAfterMin?: number
  staleAfterMin?: number
}) {
  const { name: pms } = usePmsName()
  if (!syncedAt) {
    return (
      <span style={{ ...baseStyle, ...mutedStyle }} title={`No ${pms} sync recorded yet`}>
        <Database size={11} aria-hidden="true" />
        Not yet synced
      </span>
    )
  }
  const t = new Date(syncedAt).getTime()
  const now = Date.now()
  const minutes = Math.max(0, Math.round((now - t) / 60000))
  const tone =
    minutes >= staleAfterMin ? staleStyle :
    minutes >= warnAfterMin  ? warnStyle  :
                               okStyle
  return (
    <span style={{ ...baseStyle, ...tone }} title={`Last ${pms} sync: ${new Date(syncedAt).toLocaleString()}`}>
      <Database size={11} aria-hidden="true" />
      Synced {formatDuration(minutes)} ago
    </span>
  )
}

function formatDuration(m: number) {
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m`
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ${m % 60}m`
  return `${Math.floor(m / (60 * 24))}d`
}

const baseStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: 500, padding: '2px 8px',
  borderRadius: 10, lineHeight: 1.5,
}
const okStyle: React.CSSProperties    = { background: '#dcfce7', color: '#166534' }
const warnStyle: React.CSSProperties  = { background: '#fef3c7', color: '#854d0e' }
const staleStyle: React.CSSProperties = { background: '#fee2e2', color: '#991b1b' }
const mutedStyle: React.CSSProperties = { background: '#f1f5f9', color: '#475569' }
